import type { SaveState } from "../state/AnnotationStore";

export interface SaveStatePillProps {
  state: SaveState;
  lastSavedAt: number | null;
  pendingCount?: number;
}

function relative(ms: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export function SaveStatePill({ state, lastSavedAt, pendingCount }: SaveStatePillProps): JSX.Element | null {
  if (state === "idle") return null;
  const count = pendingCount ?? 0;
  const palette: Record<SaveState, { bg: string; fg: string; text: string; title?: string }> = {
    idle:    { bg: "transparent", fg: "transparent", text: "" },
    dirty:   { bg: "#eee", fg: "#444", text: "Unsaved changes" },
    saving:  { bg: "#eee", fg: "#444", text: "Saving…" },
    saved:   { bg: "#dff5e1", fg: "#1a6b32", text: lastSavedAt !== null ? `Saved ${relative(lastSavedAt)}` : "Saved" },
    conflict:{ bg: "#fff1d6", fg: "#9a5b00", text: "Conflict — Refresh" },
    failed:  { bg: "#fde2e2", fg: "#a02020", text: "Save failed — Retry" },
    pending: {
      bg: "#fff1d6", fg: "#9a5b00",
      text: `Pending (${count})`,
      title: `${count} change${count === 1 ? "" : "s"} pending — will upload when reconnected`,
    },
    uploading: {
      bg: "#eee", fg: "#444",
      text: `Uploading (${count})…`,
      title: "Uploading queued changes",
    },
    "auth-expired": {
      bg: "#fde2e2", fg: "#a02020",
      text: "Sign in to upload",
      title: `Your session expired. Sign in to upload ${count} pending change${count === 1 ? "" : "s"}.`,
    },
  };
  const p = palette[state];
  return (
    <span
      data-testid="save-state-pill"
      data-tour="viewer.savestate"
      title={p.title}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        background: p.bg,
        color: p.fg,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {p.text}
    </span>
  );
}
